import React, { useCallback, useEffect, useState } from 'react';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';

import { useLazyQuery, useMutation } from '@apollo/client';
import * as Lo from 'lodash';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  YupObject,
  promiseModalInput,
} from 'motion-components';

import { useHandleError } from 'shared-pages/services';

import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import { useRootStore } from 'store';
import {
  FormInput,
  FormLinesInput,
  gqlAdd,
  gqlDelete,
  gqlGetByIdQuery,
  gqlUpdate,
} from '../graphql';
import FormFields from './form-fields';
import { convertFieldToInt } from 'shared-pages/common/form-helpfunc';
import { ContainersOrderStates, ContainersOrderTypes } from 'shared-pages/shared/constants';
import { formatString } from 'shared-pages/common/string-helpfunc';

const ContainerOrderFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const { handleGraphqlError } = useHandleError('containerOrder');
  const [initialNewRecData, setInitialNewRecData] = useState<any>();
  const history = useHistory();
  const location: any = useLocation();
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [addNewRecord] = useMutation(gqlAdd);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  const { t } = useTranslation();
  /** Fetch data or Initialize new record */
  useEffect(() => {
    if (!isNew) {
      get({ variables: { id: idNum } });
    } else {
      initializeNewRecord();
    }
  }, [id]);
  const initializeNewRecord = () => {
    const newRecData = {
      dateOrdered: new Date(),
      statusId: ContainersOrderStates.Pending,
      containerorderTypeId: ContainersOrderTypes.ReplaceContainer,
      lines: [],
    };
    setInitialNewRecData(newRecData);
  };
  const formShape = {
    id: YupNumeric,
    orderNo: Yup.string().nullable(),
    notes: Yup.string().nullable(),
    drivernotes: Yup.string().nullable(),
    statusId: YupNumeric,
    dateOrdered: Yup.date()
      .required('Η ημερομηνία είναι υποχρεωτική')
      .typeError('Η τιμή πρέπει να είναι ημερομηνία')
      .test('valid-format', 'Μη έγκυρη μορφή ημερομηνίας (YYYY-MM-DD)', (value) => {
        return value instanceof Date && !isNaN(value.getTime());
      })
      .default(() => new Date()),
    datePosted: YupDate,
    sendDocumentId: YupString.nullable(),
    containerorderTypeId: Yup.number()
      .typeError('Ο Τύπος Παραγγελίας είναι υποχρεωτικός')
      .required('Ο Τύπος Παραγγελίας είναι υποχρεωτικός'),
    collectionPointId: Yup.number()
      .typeError('Το Σημείο Συλλογής είναι υποχρεωτικό')
      .required('Το Σημείο Συλλογής είναι υποχρεωτικό'),

    collectorId: Yup.number().nullable(),
    collectionpoint: Yup.object().shape({
      name: Yup.string().nullable(),
      addressLine1: Yup.string().nullable(),
      addressLine2: Yup.string().nullable(),
      addressArea: Yup.string().nullable(),
      addressCity: Yup.string().nullable(),
      addressNomos: Yup.string().nullable(),
      profession: Yup.object().shape({
        id: Yup.number().nullable(),
        name: Yup.string().nullable(),
      }),
    }),
    isApproved: Yup.boolean().nullable(),
    judgeNotes: Yup.string().nullable(),
    judgedByUser: Yup.string().nullable(),
    judgeDate: Yup.date().nullable(),

    lines: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().nullable(),
          containerTypeId: Yup.number().required('Το Απόβλητο είναι υποχρεωτικό'), // Required
          containertype: Yup.object()
            .shape({
              id: Yup.number().nullable(),
              name: Yup.string().nullable(),
            })
            .nullable(),
          quantity: Yup.number()
            .test(
              'non-zero',
              t('Η τιμή πρέπει να είναι διάφορη του μηδενός'),
              (value) => value > 0 || value < 0,
            )
            .required('Η ποσότητα είναι υποχρεωτική'), // Required
        }),
      )
      .min(1, 'Πρέπει να προσθέσετε τουλάχιστον ένα Τύπο Συσκευασίας στην Αίτηση'),

    collector: Yup.object().shape({
      id: Yup.number().nullable(),
      name: Yup.string().nullable(),
    }),
  };
  const validationSchema = Yup.object().shape(formShape);
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const { authStore, appStore } = useRootStore();
  /**
   * Submit Form
   * */
  const handleSubmit = async (values: FormValuesType, actions: FormikHelpers<FormValuesType>) => {
    console.log('handleSubmit', values);
    const mutValues: any = Lo.pick(values, Lo.keys(new FormInput()));

    (mutValues as any).lines = (mutValues as any).lines.map((values) => {
      convertFieldToInt(values, ['quantity']);
      return Lo.pick(values, Lo.keys(new FormLinesInput()));
    });

    console.log('mutValues', mutValues);
    if (isNew) {
      try {
        const d = await addNewRecord({
          variables: { data: mutValues },
        });
        const id = d.data[Object.keys(d.data)[0]].id;
        toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${id}]`);
        // goto new rec
        history.push({ pathname: generatePath(getRouteByName('ContainerOrder'), { id: id }) });
      } catch (error) {
        handleGraphqlError('mutation-add ', error);
      }
    } else {
      try {
        console.log('updateRecord', mutValues);
        // Υπολόγισε Status απο τα πεδία IsApproved και DatePosted
        let calcStatus = null;
        if (mutValues.isApproved === false) {
          calcStatus = null;
        } else if (mutValues.isApproved === true) {
          calcStatus = ContainersOrderStates.Pending;
          if (mutValues.isApproved && mutValues.datePosted && mutValues.datePosted !== null) {
            calcStatus = ContainersOrderStates.Executed;
          }
        }
        mutValues.statusId = calcStatus;

        const d = await updateRecord({ variables: { data: { ...mutValues, id: idNum } } });
        toastSuccess(`Επιτυχημένη Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
        refetch();
      } catch (error) {
        handleGraphqlError('mutation-add ', error);
      }
    }
  };
  const handleDelete = useCallback(async () => {
    const dlg = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Παραγγελία',
    });
    if (dlg) {
      try {
        const d = await deleteRecord({ variables: { id: idNum } });
        toastSuccess(`Επιτυχημένη Διαγραφή Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
        history.push({ pathname: generatePath(getRouteByName('Orders')) });
      } catch (error) {
        handleGraphqlError('mutation-delete', error);
      }
    }
  }, [deleteRecord, handleGraphqlError, history, idNum]);

  async function handleAcceptRequest(values) {
    const confirmed = await promiseModalDialog({
      title: t('Έγκριση Παραγγελίας'),
      text: formatString(t('Να Εγκριθεί η Παραγγελία ({0});'), values.orderNo),
    });
    if (!confirmed) return;
    const data = {
      id: values.id,
      isApproved: true,
      judgedByUser: appStore.user,
      judgeDate: new Date(),
      judgeNotes: '',
    };

    const d = await updateRecord({ variables: { data } });
    toastSuccess(`Επιτυχημένη Αποδοχή Παραγγελίας [${data.id}]`);
    refetch();
  }

  async function handleRejectRequest(values) {
    // Prompt user for reason
    const reason = await promiseModalInput({
      title: 'Αιτιολόγηση Απόρριψης',
      text: 'Αιτιολογήστε τον λόγο της απόρριψης:',
    });

    // if user canceled or closed the dialog
    if (!reason) return;

    const data = {
      id: values.id,
      isApproved: false,
      judgedByUser: appStore.user,
      judgeDate: new Date(),
      judgeNotes: reason,
    };

    const d = await updateRecord({ variables: { data } });
    toastSuccess(`Επιτυχημένη Απόρριψη Παραγγελίας [${data.id}]`);
    refetch();
  }

  const isModal = location.state && location.state.modal;
  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('ContainerOrders')));
  }, [history, location.state]);

  return (
    <>
      <FormCard
        title={idNum === -1 ? 'Νέα Παραγγελία Συσκευασιών' : `Παραγγελία Συσκευασιών [${idNum}]`}
        validationSchema={validationSchema}
        initialValues={isNew ? initialNewRecData : pickProperties(data)}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        permitSubmit={false}
        permitDelete={false}
        enableReinitialize
        idx={idNum}
        onReturn={!isModal ? handleReturn : undefined}
      >
        <FormFields
          isNew={isNew}
          data={data}
          acceptRequest={(values) => handleAcceptRequest(values)}
          rejectRequest={(values) => handleRejectRequest(values)}
          validationSchema={validationSchema}
        />
      </FormCard>
    </>
  );
};
export default ContainerOrderFormPage;
